<!-- CriteriaManager.svelte -->
<script>
	import { tick, onMount } from "svelte"; // keep
	import {
		Criteria,
		CriteriaCategorization,
		CriteriaPolarity,
	} from "./UserData.js";
	import {
		suggestedItemsMap,
		suggestedCriteria,
	} from "./SuggestedCriteria.js";

	/*------------- state & props -------------*/
	let newItem = $state("");
	// Reactive list of names for the UI (keys of the centralized map)
	let suggestedItems = $state(Object.keys(suggestedItemsMap));
	// Counter to track the next available custom icon number (1-5)
	let nextCustomIconNumber = $state(1);

	const getCustomIconPath = (index) => {
		if (index >= 1 && index <= 5) {
			return `/Icons/Custom Criteria Icon - Signpost ${index}.svg`;
		}
		return "/Icons/Custom Criteria Icon - Signpost base.svg";
	};

	let {
		userItems = $bindable([]), // ← stays two-way-bindable
		iconMapping = {
			"Gut feeling": "/Icons/Gut feeling icon.svg",
			"Cost (Time/Money...)": "/Icons/Cost icon.svg",
			"Long term benefit": "/Icons/Long term benefit icon 3.svg",
			"Main Goal": "/Icons/Main goal icon.png",
			default: "/Icons/Custom Criteria Icon - Signpost base.svg",
		},
	} = $props();

	/*------------- helpers -------------*/
	const scrollToElement = (selector, block) => {
		const el = document.querySelector(selector);
		if (el) el.scrollIntoView({ behavior: "smooth", block });
	};

	const addItem = async (itemName, doScroll = true) => {
		// Check if the item is already in the list or if the name is empty
		if (
			userItems.some((i) => i.name === itemName) ||
			itemName.trim() === ""
		)
			return;

		// Determine the category based on the number of items in userItems
		const category = userItems.length >= 3 ? "Important" : "Critical";
		// Add the new item to userItems with the appropriate icon
		const polarity = suggestedItemsMap[itemName] || "Higher is better";
		const isCustom = !suggestedItemsMap[itemName];

		let iconPath = iconMapping[itemName];
		if (isCustom) {
			iconPath = getCustomIconPath(nextCustomIconNumber);
			console.log("Assigning custom icon:", iconPath, "to", itemName);
			// Update the next available icon number (cycle 1-5)
			nextCustomIconNumber = (nextCustomIconNumber % 5) + 1;
		} else {
			iconPath = iconMapping[itemName] || iconMapping.default;
		}

		userItems = [
			...userItems,
			{
				name: itemName,
				category,
				polarity,
				icon: iconPath, // Store the icon path with the item
			},
		];
		// Remove the added item from suggestedItems
		suggestedItems = suggestedItems.filter((i) => i !== itemName);

		if (doScroll) {
			// Wait for DOM update then scroll
			await tick();
			scrollToElement(".add-factor", "end");
		}
	};

	const removeItem = (name) => {
		userItems = userItems.filter((i) => i.name !== name);
		if (!suggestedItems.includes(name)) {
			suggestedItems = [...suggestedItems, name];
		}
	};

	const toggleCategory = (item) => {
		const next = item.category === "Critical" ? "Important" : "Critical";
		userItems = userItems.map((i) =>
			i.name === item.name ? { ...i, category: next } : i,
		);
	};

	const togglePolarity = (item) => {
		const next =
			item.polarity === "Higher is better"
				? "Lower is better"
				: "Higher is better";
		userItems = userItems.map((i) =>
			i.name === item.name ? { ...i, polarity: next } : i,
		);
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		addItem(newItem);
		newItem = "";
	};

	const handleKeydown = (itemName) => (e) => {
		if (e?.key === "Enter") addItem(itemName);
	};

	// Pre-select default criteria (IDs 3 and 4) once on component mount
	const defaultCriteriaIds = ["3", "4"];
	onMount(() => {
		if (userItems.length === 0) {
			const defaultNames = suggestedCriteria
				.filter((c) => defaultCriteriaIds.includes(c.id))
				.map((c) => c.name);
			defaultNames.forEach(name => addItem(name, false)); // el False hace que no se haga scroll
		}
	});

	/*------------- side-effects -------------*/
	$effect(() => {
		// Update the Criteria store with full criterion objects
		Criteria.set([...userItems]);

		// For backward compatibility, also update the separate stores
		const categories = userItems.map((i) => i.category);
		const polarities = userItems.map((i) => i.polarity);

		CriteriaCategorization.set(categories);
		CriteriaPolarity.set(polarities);

		console.log("userItems:", $state.snapshot(userItems));
	});
</script>

<div
	class="criteria-manager"
	style="width:85vw;max-width:600px;margin-bottom:20px;"
>
	<!-- list of selected criteria -->
	<div style="margin-bottom:60px">
		<ul class="item-list">
			{#each userItems as item (item.name)}
				<li class="item">
					<div class="columns">
						<div
							class="column is-flex is-flex-direction-column is-align-items-center"
							style="margin-right:0.5em;"
						>
							<img
								src={item.icon ||
									iconMapping[item.name] ||
									iconMapping.default}
								alt={item.name}
								class="item-icon"
							/>

							<div
								class={item.category === "Critical"
									? "tag is-primary is-light"
									: "tag is-warning is-light"}
								role="button"
								tabindex="0"
								onclick={() => toggleCategory(item)}
								onkeydown={() => toggleCategory(item)}
							>
								{item.category}
							</div>
						</div>
					</div>

					<span style="text-align:center;">{item.name}</span>

					<button
						type="button"
						onclick={() => removeItem(item.name)}
						class="delete is-large"
						aria-label={`Delete ${item.name}`}
					>
					</button>
				</li>
			{/each}
		</ul>
	</div>

	<p
		style="text-align:center;padding:1.5em;margin-top:-1em;margin-bottom:1em;"
	>
		Choose from our curated list of <strong>calibrated questions</strong>
	</p>

	<!-- suggested criteria grid -->
	<div class="grid-container">
		{#each suggestedItems as item (item)}
			<div
				class="grid-item"
				title={item}
				role="button"
				tabindex="0"
				onclick={() => addItem(item)}
				onkeydown={handleKeydown(item)}
			>
				<img
					src={iconMapping[item] || iconMapping.default}
					alt={item}
					class="icon"
				/>
			</div>
		{/each}
	</div>

	<p style="text-align:center;padding:0.5em;">
		or add your own <strong>factors</strong> to guide this decision
	</p>

	<!-- add-factor form -->
	<div class="add-factor">
		<ul class="add-factor">
			<form onsubmit={handleSubmit} class="add-item-form">
				<input
					type="text"
					bind:value={newItem}
					placeholder="Add factor"
				/>
				<button type="submit" class="add-button" aria-label="Add factor"
					>+</button
				>
			</form>
		</ul>
	</div>
</div>

<style>
	.tag {
		margin-top: 0.5em;
	}

	.tag.is-primary {
		background-color: #9cfffa;
		color: #084b83;
	}

	.tag.is-warning {
		background-color: #ffec6c;
	}

	@media (max-width: 600px) {
		/* Hacer el grid responsive */
		.grid-container {
			grid-template-columns: repeat(2, 1fr);
		}
	}

	.item {
		display: flex;
		align-items: center; /* Vertically align items in the center */
		justify-content: space-between; /* Space items out */
		padding: 8px 10px;
		background-color: #fff;
		border-radius: 5px;
		margin-bottom: 10px;
		margin-right: 1em;
		box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
	}

	.grid-container {
		display: grid;
		grid-template-columns: repeat(
			3,
			1fr
		); /* Adjust number of columns as needed */
		gap: 0.5em;
		margin-bottom: 20px;
	}

	.grid-item {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 10px;
		background-color: #fff;
		border-radius: 5px;
		box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
		cursor: pointer;
	}

	.grid-item:hover {
		background-color: #f0f0f0;
	}

	.grid-item:focus {
		outline: 2px solid #9cfffa; /* Change as per your design */
	}

	.icon {
		width: 50px; /* Adjust as necessary */
		height: auto;
		max-height: 50px;
	}

	.item-icon {
		width: 2em; /* Adjust as necessary */
		height: auto; /* Adjust as necessary */
		/* margin-right: 1em; Space between icon and text */
	}

	.add-item-form {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 8px 10px;
		background-color: #fff;
		border-radius: 5px;
		margin-bottom: 10px;
		box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
	}

	input[type="text"] {
		width: 90%;
		padding: 10px;
		margin-bottom: 10px;
	}

	ul {
		width: 100%;
		list-style-type: none;
		padding: 0;
	}

	button.add-button,
	button.delete {
		/* padding: 10px 15px; */
		/* margin-left: auto; */
		margin-left: -0.8em;
		border-radius: 7px;
		/* align-self:start; */
	}

	button.add-button {
		color: #084b83;
	}

	.add-item-form:hover {
		background-color: #f0f0f0;
	}

	/* .remove-button:hover {     													ME LO LLEVO A GLOBAL!
				background-color: #EF8275; Darken button on hover for feedback
				background-color: #d32f2f; Darken button on hover for feedback
			} */
</style>
